import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { brand } from "../../../theme/businessFlowsTheme";
import { colourName, swatchOf } from "./names";

const HEX = /^#[0-9a-fA-F]{6}$/;

// A colour chosen from the accessible palette, or any hex value.
export default function ColourSwatchPicker({ value, palette = [], onChange, disabled = false, label = "Colour" }) {
  const [anchor, setAnchor] = useState(null);
  const [custom, setCustom] = useState(value || "");
  useEffect(() => setCustom(value || ""), [value]);

  const choose = (colour) => {
    setAnchor(null);
    if (colour !== value) onChange(colour);
  };

  return (
    <>
      <Tooltip title={label}>
        <span>
          <ButtonBase
            disabled={disabled}
            aria-label={`${label}: ${colourName(value, palette)}`}
            onClick={(e) => {
              e.stopPropagation();
              setAnchor(e.currentTarget);
            }}
            sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: value, border: "2px solid #fff", boxShadow: `0 0 0 1px ${brand.line}`, flexShrink: 0 }}
          />
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: "bfx", sx: { p: 1.5, borderRadius: 3, width: 240 } }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 1 }}>
          {palette.map(swatchOf).map(({ value: colour, name }) => (
            <ButtonBase
              key={colour}
              aria-label={name ? `Use ${name} (${colour})` : `Use ${colour}`}
              title={name || colour}
              aria-pressed={colour === value}
              onClick={() => choose(colour)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: colour,
                boxShadow: colour === value ? `0 0 0 2px #fff, 0 0 0 4px ${brand.ink}` : "none",
                "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
              }}
            />
          ))}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            label="Hex"
            fullWidth={false}
            value={custom}
            error={Boolean(custom) && !HEX.test(custom)}
            onChange={(e) => setCustom(e.target.value.trim())}
            inputProps={{ maxLength: 7 }}
            sx={{ flex: 1 }}
          />
          <Button size="small" variant="contained" disabled={!HEX.test(custom)} onClick={() => choose(custom)}>
            Use
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
